import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  Package, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCcw, 
  Search, 
  History,
  CreditCard,
  ChevronRight,
  Tag,
  Check,
  Receipt
} from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  orderBy,
  doc,
  getDoc,
  updateDoc,
  limit
} from 'firebase/firestore';
import { useRealtime } from '../../contexts/RealtimeContext';
import { initializeRouteCheckout } from '../../utils/routePaymentHelper';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const StudentStore = () => {
  const { userProfile, currentUser, refreshUserProfile } = useRealtime();
  const [activeTab, setActiveTab] = useState('shop'); // 'shop' or 'history'
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [quantities, setQuantities] = useState({}); // { itemId: quantity }
  const [coachingId, setCoachingId] = useState(userProfile?.coachingId);

  // Get student info
  const studentName = userProfile?.displayName || userProfile?.name || 'Student';
  const studentEmail = userProfile?.email || currentUser?.email;
  const studentPhone = userProfile?.phone || '';

  // Handle coachingId linking and fallback
  useEffect(() => {
    if (userProfile?.coachingId) {
      setCoachingId(userProfile.coachingId);
    } else if (currentUser?.email) {
      const checkEnrollmentsFallback = async () => {
        try {
          const userEmail = currentUser.email.toLowerCase().trim();
          
          // Try batchEnrollments first (most likely source)
          const enrollmentsRef = collection(db, 'batchEnrollments');
          const q = query(enrollmentsRef, where('email', '==', userEmail), limit(1));
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const foundId = snapshot.docs[0].data().coachingId;
            if (foundId) {
              setCoachingId(foundId);
              // Update profile for future loads
              await updateDoc(doc(db, 'users', currentUser.uid), { 
                coachingId: foundId,
                updatedAt: serverTimestamp()
              });
              return;
            }
          }

          // Try students collection as second fallback
          const studentsRef = collection(db, 'students');
          const studentQ = query(studentsRef, where('email', '==', userEmail), limit(1));
          const studentSnapshot = await getDocs(studentQ);
          
          if (!studentSnapshot.empty) {
            const foundId = studentSnapshot.docs[0].data().coachingId;
            if (foundId) {
              setCoachingId(foundId);
              // Update profile for future loads
              await updateDoc(doc(db, 'users', currentUser.uid), { 
                coachingId: foundId,
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (error) {
          console.error('Error in StudentStore fallback check:', error);
        }
      };
      checkEnrollmentsFallback();
    }
  }, [userProfile?.coachingId, currentUser?.email, currentUser?.uid]);

  useEffect(() => {
    if (coachingId) {
      if (activeTab === 'shop') {
        loadStoreItems();
      } else {
        loadOrderHistory();
      }
    } else {
      setLoading(false);
    }
  }, [coachingId, activeTab]);

  const loadStoreItems = async () => {
    try {
      setLoading(true);
      const storeRef = collection(db, COLLECTIONS.STORE_ITEMS || 'storeItems');
      const q = query(storeRef, where('coachingId', '==', coachingId), where('inStock', '==', true));
      const snapshot = await getDocs(q);
      
      const itemsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setItems(itemsData);
    } catch (error) {
      console.error('Error loading store items:', error);
      toast.error('Failed to load store items');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderHistory = async () => {
    try {
      setLoading(true);
      const ordersRef = collection(db, COLLECTIONS.STORE_ORDERS || 'storeOrders');
      
      let ordersData = [];
      try {
        // Try optimized query with orderBy (requires composite index)
        const q = query(
          ordersRef, 
          where('studentId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
      } catch (indexError) {
        console.warn('StudentStore: Missing index for orders query, falling back to client-side sort:', indexError);
        // Fallback: Simple query without orderBy
        const fallbackQ = query(
          ordersRef, 
          where('studentId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(fallbackQ);
        ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        
        // Sort in memory
        ordersData.sort((a, b) => b.createdAt - a.createdAt);
      }
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading order history:', error);
      toast.error('Failed to load your order history');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (item) => {
    const quantity = quantities[item.id] || 1;
    const totalAmount = item.price * quantity;

    if (item.quantityInStock < quantity) {
      toast.error(`Only ${item.quantityInStock} units available in stock`);
      return;
    }

    setProcessingId(item.id);
    try {
      // 1. Get coaching admin UID to route payment
      let adminId = null;
      
      // Step A: Check coachingCenters collection first
      const coachingDoc = await getDoc(doc(db, 'coachingCenters', coachingId));
      if (coachingDoc.exists()) {
        adminId = coachingDoc.data().adminId;
      }

      // Step B: If not found, search users collection for coaching_admin with this coachingId
      if (!adminId) {
        console.log('StudentStore: AdminId not found in coachingCenters, searching users collection...');
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef, 
          where('coachingId', '==', coachingId), 
          where('role', '==', 'coaching_admin'),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          adminId = snapshot.docs[0].id;
          console.log('StudentStore: Found adminId in users collection:', adminId);
        }
      }

      if (!adminId) {
        throw new Error('This institution has not set up online payments yet.');
      }

      // Step C: Verify admin is actually linked to Razorpay (since user said they can pay fees)
      const adminSnap = await getDoc(doc(db, 'users', adminId));
      if (adminSnap.exists()) {
        const adminData = adminSnap.data();
        if (!adminData.razorpayLinkedAccountId) {
          console.warn('StudentStore: Admin found but razorpayLinkedAccountId is missing.');
          // We'll proceed anyway as initializeRouteCheckout might handle it, 
          // but if they can pay fees, it SHOULD be there.
        }
      }

      // 2. Initialize Razorpay Checkout
      await initializeRouteCheckout({
        amount: totalAmount,
        recipientUserId: adminId,
        paymentType: 'store_purchase',
        payerDetails: {
          name: studentName,
          email: studentEmail,
          phone: studentPhone
        },
        notes: {
          itemId: item.id,
          itemName: item.name,
          quantity,
          coachingId,
          studentId: currentUser.uid
        },
        razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_G66qKof8D9C6Fv', // Fallback for dev
        onSuccess: async (response) => {
          // 3. Create order in Firestore after successful payment
          const orderRef = collection(db, COLLECTIONS.STORE_ORDERS || 'storeOrders');
          await addDoc(orderRef, {
            studentId: currentUser.uid,
            studentName,
            studentEmail,
            coachingId,
            items: [{
              id: item.id,
              name: item.name,
              price: item.price,
              quantity: quantity,
              size: item.sizes?.[0] || null
            }],
            totalAmount: totalAmount,
            status: 'pending',
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            stockAdjusted: false // This will be handled by admin accepting the order
          });

          toast.success('Order placed successfully! Check History for status.');
          setProcessingId(null);
          setQuantities(prev => ({ ...prev, [item.id]: 1 }));
          loadStoreItems();
        },
        onFailure: (error) => {
          toast.error(error.error || 'Payment failed');
          setProcessingId(null);
        }
      });
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to initialize purchase');
      setProcessingId(null);
    }
  };

  const generateReceipt = async (order) => {
    try {
      const docReceipt = new jsPDF();
      const pageWidth = docReceipt.internal.pageSize.getWidth();
      
      // Fetch institute info from admin profile
      let adminId = null;
      const coachingSnap = await getDoc(doc(db, 'coachingCenters', coachingId));
      if (coachingSnap.exists()) {
        adminId = coachingSnap.data().adminId;
      }

      if (!adminId) {
        // Fallback search for adminId for receipt info
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef, 
          where('coachingId', '==', coachingId), 
          where('role', '==', 'coaching_admin'),
          limit(1)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          adminId = snapshot.docs[0].id;
        }
      }

      let institute = {};
      let instituteName = 'Coaching Institute';
      
      if (adminId) {
        const adminSnap = await getDoc(doc(db, 'users', adminId));
        if (adminSnap.exists()) {
          const adminData = adminSnap.data();
          institute = adminData.reportCardInstituteDefaults || {};
          instituteName = adminData.coachingName || adminData.institutionName || instituteName;
        }
      }

      // Header - Institute Info
      docReceipt.setFontSize(22);
      docReceipt.setFont(undefined, 'bold');
      docReceipt.setTextColor(30, 41, 59);
      docReceipt.text(instituteName, pageWidth / 2, 20, { align: 'center' });

      docReceipt.setFontSize(10);
      docReceipt.setFont(undefined, 'normal');
      docReceipt.setTextColor(100, 116, 139);
      
      let y = 28;
      if (institute.affiliationNumber) {
        docReceipt.text(`Affiliation No.: ${institute.affiliationNumber}`, pageWidth / 2, y, { align: 'center' });
        y += 5;
      }
      if (institute.address) {
        const addressLines = docReceipt.splitTextToSize(institute.address, pageWidth - 40);
        addressLines.forEach(line => {
          docReceipt.text(line, pageWidth / 2, y, { align: 'center' });
          y += 5;
        });
      }
      const contactLine = [institute.instituteEmail, institute.institutePhone].filter(Boolean).join('  |  ');
      if (contactLine) {
        docReceipt.text(contactLine, pageWidth / 2, y, { align: 'center' });
        y += 5;
      }

      // Divider
      docReceipt.setDrawColor(226, 232, 240);
      docReceipt.line(20, y + 5, pageWidth - 20, y + 5);
      y += 15;

      // Receipt Title & Order Info
      docReceipt.setFontSize(16);
      docReceipt.setFont(undefined, 'bold');
      docReceipt.setTextColor(30, 41, 59);
      docReceipt.text('ORDER RECEIPT', 20, y);
      
      docReceipt.setFontSize(10);
      docReceipt.setFont(undefined, 'normal');
      docReceipt.text(`Order ID: #${order.id.slice(-8).toUpperCase()}`, pageWidth - 20, y, { align: 'right' });
      y += 10;

      docReceipt.text(`Date: ${order.createdAt.toLocaleDateString()}`, 20, y);
      docReceipt.text(`Status: ${order.status.toUpperCase()}`, pageWidth - 20, y, { align: 'right' });
      y += 15;

      // Student Info
      docReceipt.setFont(undefined, 'bold');
      docReceipt.text('Bill To:', 20, y);
      y += 5;
      docReceipt.setFont(undefined, 'normal');
      docReceipt.text(order.studentName || 'Student Name', 20, y);
      y += 5;
      docReceipt.text(order.studentEmail || '', 20, y);
      y += 15;

      // Items Table
      const tableData = order.items.map(item => [
        item.name + (item.size ? ` (${item.size})` : ''),
        item.quantity,
        `₹${item.price.toLocaleString()}`,
        `₹${(item.price * item.quantity).toLocaleString()}`
      ]);

      docReceipt.autoTable({
        startY: y,
        head: [['Item Description', 'Qty', 'Price', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        foot: [['', '', 'Grand Total:', `₹${order.totalAmount.toLocaleString()}`]],
        footStyles: { fillColor: [248, 250, 252], textColor: [30, 41, 59], fontStyle: 'bold' }
      });

      // Footer
      const finalY = docReceipt.lastAutoTable.finalY + 30;
      docReceipt.setFontSize(10);
      docReceipt.text('Authorized Signatory', pageWidth - 20, finalY, { align: 'right' });
      docReceipt.line(pageWidth - 60, finalY - 5, pageWidth - 20, finalY - 5);

      docReceipt.setFontSize(8);
      docReceipt.setTextColor(148, 163, 184);
      docReceipt.text('This is a computer generated receipt.', pageWidth / 2, docReceipt.internal.pageSize.getHeight() - 10, { align: 'center' });

      docReceipt.save(`Receipt-${order.id.slice(-6).toUpperCase()}.pdf`);
      toast.success('Receipt generated successfully');
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    }
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending': return { label: 'Pending', icon: Clock, color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30' };
      case 'progress': return { label: 'In Progress', icon: RefreshCcw, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' };
      case 'completed': return { label: 'Completed', icon: CheckCircle2, color: 'text-green-600 bg-green-100 dark:bg-green-900/30' };
      case 'returned': return { label: 'Returned', icon: AlertCircle, color: 'text-red-600 bg-red-100 dark:bg-red-900/30' };
      default: return { label: status, icon: Clock, color: 'text-gray-600 bg-gray-100' };
    }
  };

  if (!coachingId) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Store Unavailable</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2 mb-6">
          Your profile is not linked to any institution yet. Please contact your administrator.
        </p>
        <button
          onClick={async () => {
            setLoading(true);
            await refreshUserProfile();
            // The useEffect will handle the rest if coachingId is found
            setTimeout(() => setLoading(false), 2000);
          }}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold transition-all inline-flex items-center gap-2"
        >
          <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
          <span>Check for Link</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Switcher */}
      <div className="flex bg-gray-100 dark:bg-gray-700/50 p-1 rounded-xl w-full max-w-md mx-auto">
        <button
          onClick={() => setActiveTab('shop')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'shop' 
              ? 'bg-white dark:bg-gray-800 text-blue-600 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <ShoppingBag size={18} />
          Shop Store
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
            activeTab === 'history' 
              ? 'bg-white dark:bg-gray-800 text-blue-600 shadow-sm' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History size={18} />
          My Orders
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'shop' ? (
          <motion.div
            key="shop"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredItems.map((item) => (
                  <div 
                    key={item.id}
                    className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col"
                  >
                    <div className="p-6 flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-xl ${
                          item.quantityInStock < 10 ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          <Package size={24} />
                        </div>
                        <span className="text-xs px-2.5 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full font-medium">
                          {item.category}
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{item.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                        {item.description || 'No description provided.'}
                      </p>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {item.sizes?.map(size => (
                          <span key={size} className="text-[10px] px-2 py-0.5 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-md">
                            Size: {size}
                          </span>
                        ))}
                      </div>

                      <div className="mt-6 flex items-center justify-between">
                        <span className="text-2xl font-black text-gray-900 dark:text-white">₹{item.price.toLocaleString()}</span>
                        <div className="flex items-center gap-2">
                          <label className="text-xs font-bold text-gray-400">Qty:</label>
                          <select 
                            value={quantities[item.id] || 1}
                            onChange={(e) => setQuantities(prev => ({ ...prev, [item.id]: parseInt(e.target.value) }))}
                            className="bg-gray-100 dark:bg-gray-700 border-none rounded-lg text-xs font-bold px-2 py-1 outline-none"
                          >
                            {[...Array(Math.min(10, item.quantityInStock))].map((_, i) => (
                              <option key={i+1} value={i+1}>{i+1}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={() => handlePurchase(item)}
                        disabled={processingId === item.id}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                      >
                        {processingId === item.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <CreditCard size={18} />
                            <span>Buy {(quantities[item.id] || 1) > 1 ? `${quantities[item.id]} Units` : 'Now'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <ShoppingBag className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">No products found</h3>
                <p className="text-gray-500 mt-2">Check back later for new items!</p>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map((order) => {
                  const status = getStatusInfo(order.status);
                  const StatusIcon = status.icon;
                  return (
                    <div 
                      key={order.id}
                      className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-xl ${status.color}`}>
                            <StatusIcon size={24} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-gray-900 dark:text-white">
                                Order #{order.id.slice(-6).toUpperCase()}
                              </h4>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${status.color}`}>
                                {status.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {order.items?.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                            </p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Tag size={12} /> ₹{order.totalAmount?.toLocaleString()}
                              </span>
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock size={12} /> {order.createdAt.toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {order.status === 'completed' ? (
                            <button
                              onClick={() => generateReceipt(order)}
                              className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg transition-all"
                            >
                              <Receipt size={14} /> Receipt
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Tracking</span>
                          )}
                          <ChevronRight className="text-gray-300" size={20} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                <History className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">No orders yet</h3>
                <p className="text-gray-500 mt-2">Your purchase history will appear here.</p>
                <button 
                  onClick={() => setActiveTab('shop')}
                  className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
                >
                  Start Shopping
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudentStore;
